import cx from 'classnames';
import type { FunctionComponent, ReactNode } from 'react';

import useDropdown from '../../../hooks/useDropdown';
import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';
import Icon, { icons } from '../Icon';
import RouterLink from '../Link/RouterLink';

import { useSubnavContext } from './context';
import SubnavDropdownItem, { type DropdownItemComponent } from './SubnavDropdownItem';
import SubnavDropdownPaidFeatureItem, {
  type SubnavDropdownPaidFeatureItemComponent,
} from './SubnavDropdownPaidFeatureItem';

import styles from './Subnav.module.less';

type Props = {
  to: string;
  navKey: string;
  children: ReactNode;
  testId?: string;
};

export type SubnavDropdownComponent = FunctionComponent<Props> & {
  RootItem: FunctionComponent;
  Item: DropdownItemComponent;
  PaidFeatureItem: SubnavDropdownPaidFeatureItemComponent;
};

/**
 * Type of SubnavItem which allows for a dropdown menu of additional items.
 */
const SubnavDropdown: SubnavDropdownComponent = ({
  to,
  children,
  navKey,
  testId,
}) => {
  const { activeKey } = useSubnavContext();

  //  Find the RootItem and DropdownItems slots.
  const RootItem = findFirstForSlot('RootItem', children);
  const DropdownItems = findAllForSlot('Item', children);

  const {
    isOpen,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
  } = useDropdown();
  //  We check if the activeKey begins with the same navKey as the Dropdown component itself.
  const isRootItemActive = activeKey && activeKey.startsWith(navKey);

  return (
    <div
      className={styles.dropdownItem}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-test-id={testId}
    >
      <RouterLink
        to={to}
        className={cx(styles.link, {
          [styles.linkActive]: isRootItemActive,
        })}
        aria-haspopup="true"
        aria-expanded={isOpen}
        tabIndex={0}
      >
        <div id={`fossa-subnav-${navKey}`} className={styles.item}>
          {RootItem}
          <span className={styles.dropdownItemAngleDownIcon}>
            <Icon icon={icons.angleDown} />
          </span>
        </div>
      </RouterLink>
      {isOpen && (
        <div className={styles.dropdownMenuContainer}>
          <div className={styles.dropdownMenu} role="menu">
            {DropdownItems}
          </div>
        </div>
      )}
    </div>
  );
};

SubnavDropdown.RootItem = defineSlotComponent('RootItem', ({ children }) => <>{children}</>);
SubnavDropdown.Item = defineSlotComponent('Item', SubnavDropdownItem);
//  Note that this is also using the "Item" slot, because we want SubnavDropdown to treat it like a regular item (in terms of array order).
SubnavDropdown.PaidFeatureItem = defineSlotComponent('Item', SubnavDropdownPaidFeatureItem);

export default SubnavDropdown;
